<template>
  <VApp>
    <VMain>
      <span v-if="isPublicRoute">
        <div class="pa-0 page-container">
          <RouterView />
        </div>
      </span>

      <Authenticator
        v-else
        hide-sign-up
        :formFields="formFields"
        :services="services"
        :key="refreshKey"
      >
        <template v-slot:header>
          <LoginBackground />
          <logo class="currentColor" />
          <p class="currentColor center-text">
            {{ t('we-make-it-easier-to-run-great-restaurants') }}
          </p>
        </template>

        <template v-slot:sign-in-footer>
          <button @click="toResetPassword" class="forgot-password-button mb-3">
            {{ t('forgot-your-password') }}
          </button>

          <div class="dividerContainer">
            <div class="dividerWithText">
              <span>{{ t('or') }}</span>
            </div>
          </div>

          <a class="buttonContainer" :href="mcdUrl">
            <button
              class="amplify-button amplify-field-group__control amplify-authenticator__font eidButton"
            >
              {{ t('sign-in-with-mcd-eid') }}
            </button>
          </a>

          <MagcLinkButton />

          <div class="text-center py-1">
            <VBtnTertiary @click="toggleLocale()">
              {{ translateButtonLabel }}
            </VBtnTertiary>
          </div>
        </template>

        <template v-slot:footer>
          <p class="currentColor pa-2 center-text">
            QSRSoft, LLC {{ t('confidential') }}
          </p>

          <p class="currentColor center-text">
            © 2019-{{ currentYear }} QSRSoft, LLC.
            {{ t('all-rights-reserved') }}
          </p>
        </template>

        <template v-slot="{ signOut }">
          <TopNav
            v-if="!hideTopBar"
            :signOut="signOut"
            v-model:changingClient="changingClient"
            v-model:showChangeClient="showChangeClient"
            v-model:settingAssumeUser="settingAssumeUser"
            v-model:showNavBar="showNavBar"
            v-model:multiOrgUserSettingOrg="multiOrgUserSettingOrg"
            :hideSideBar="hideSideBar"
            :hasExtraContent="!!$slots['top-nav']"
            ref="topNav"
          >
            <slot
              name="top-nav"
              v-if="user && !changingClient && !settingAssumeUser"
            />
          </TopNav>

          <SideBar
            v-if="!hideSideBar"
            :routes="routes"
            v-model:showNavBar="showNavBar"
          />

          <div v-if="failedToLoad" class="loading-container">
            <div>
              {{ t('failed-to-load-please-click-this-button') }}
              <VBtn color="primary" @click="reload">{{ t('reload') }}</VBtn>
            </div>
          </div>

          <div v-else-if="changingClient" class="changing-client-loading">
            <VProgressCircular indeterminate />
          </div>

          <ChangeClient
            v-else-if="showChangeClient"
            @close="showChangeClient = false"
          />

          <AssumeUser
            v-else-if="settingAssumeUser"
            @close="settingAssumeUser = false"
          />

          <TOS v-else-if="showTOS" @close="showTOS = false" />

          <EmailPopup
            v-else-if="showEmailPopup"
            v-model="showEmailPopup"
            @close="showEmailPopup = false"
          />

          <template v-else>
            <div v-if="!user" class="loading-container">
              <div>
                {{ t('wait-a-minute-while-we-load-your-app') }}
                <VProgressCircular indeterminate />
              </div>
            </div>

            <MultiClientUserOrgSelect
              v-else-if="!orgName || multiOrgUserSettingOrg"
              v-model:multiOrgUserSettingOrg="multiOrgUserSettingOrg"
            />

            <template v-else>
              <template
                v-if="useCustomOrgAccess ? !customOrgAccess : !orgHasApp"
              >
                <div class="loading-container">
                  <div>
                    {{ t('your-org-does-not-have-access-to-this-application') }}
                  </div>
                </div>
              </template>

              <template
                v-else-if="
                  useCustomHasPermission ? !customHasPermission : !hasPermission
                "
              >
                <div class="loading-container">
                  <div>
                    {{ t('you-do-not-have-access-to-this-application') }}
                  </div>
                </div>
              </template>

              <template v-else>
                <SupportBanner v-if="!hideSupportBanner" />

                <PageHeader ref="pageHeader" v-if="!hidePageHeaderComputed" />

                <div class="page-container" :class="hidePadding ? '' : 'pa-2'">
                  <slot />
                </div>
                <DataPassAlert />

                <BottomNav
                  :routes="routes"
                  v-if="loggedIn && smAndDown && isCrewMember && !hideBottomNav"
                />
              </template>
            </template>
          </template>
        </template>
      </Authenticator>
      <DebugWindow />
      <DebugNotifier />
    </VMain>
  </VApp>
</template>

<script setup>
import { ref, toRefs, computed, watch, provide, onBeforeMount } from 'vue';
import { useTheme, useDisplay } from 'vuetify';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import { Authenticator, useAuthenticator } from '@aws-amplify/ui-vue';
import { Amplify, Auth, Hub, I18n } from 'aws-amplify';
import '@aws-amplify/ui-vue/styles.css';
import { translations as amplifyTranslations } from '@aws-amplify/ui-vue';

import { useRootStore } from './stores/root';
import awsExports from './aws-exports';
import usePermissions from './composables/usePermissions';
import { addRoutes, addPublicRoutes } from './router';
import { checkTOSMismatch } from './helpers/checkTOSMismatch';

import { isString } from './helpers/type_guards';
import { getMcDSignInURL } from './helpers/auth';
import { setV3LogoutCookie, unsetV3LogoutCookie } from './helpers/v2AuthLoader';
import { eidLogout } from './helpers/eid_logout';
import { setLocale, getBrowserLocale } from './helpers/i18n';

// https://ui.docs.amplify.aws/vue/connected-components/authenticator
import ChangeClient from './components/admin/ChangeClient.vue';
import AssumeUser from './components/admin/AssumeUser.vue';

import LoginBackground from './components/LoginBackground.vue';
import SupportBanner from './components/bottomSideBar/whatsNew/SupportBanner.vue';
import DataPassAlert from './components/datapass/data_pass_alert.vue';
import logo from './assets/logo.vue';
import TopNav from './components/topNav/TopNav.vue';
import SideBar from './components/SideBar.vue';
import PageHeader from './components/PageHeader.vue';
import BottomNav from './components/bottomNav/BottomNav.vue';
import TOS from './components/TOS.vue';
import EmailPopup from './components/EmailPopup.vue';
import MultiClientUserOrgSelect from './components/MultiClientUserOrgSelect.vue';
import MagcLinkButton from './components/magicLink/magic_link_button.vue';
import DebugWindow from './components/debug/DebugWindow.vue';
import DebugNotifier from './components/debug/DebugNotifier.vue';
import { accountsGet } from './helpers/api';
import { getRum } from './helpers/rum';

const { t } = useI18n();
const refreshKey = ref(0);

const { smAndDown } = useDisplay();
const route = useRoute();
const router = useRouter();
const showTOS = ref(false);
const showEmailPopup = ref(false);

const formFields = {
  signIn: {
    username: {
      label: I18n.get('email'),
      placeholder: I18n.get('email'),
      isRequired: true,
    },
    password: {
      label: I18n.get('password'),
      placeholder: I18n.get('password'),
      isRequired: true,
    },
  },
  resetPassword: {
    username: {
      label: I18n.get('username'),
      placeholder: I18n.get('enter-your-username'),
    },
  },
};

const services = {
  handleSignIn(input) {
    const { username } = input;
    const lowerCaseUsername = username.toLowerCase();
    return Auth.signIn({
      ...input,
      username: lowerCaseUsername,
    });
  },
};
const { toResetPassword } = useAuthenticator();

const props = defineProps({
  routes: {
    type: Array,
    default: () => [],
  },
  publicRoutes: {
    type: Array,
    default: () => [],
  },
  appPermissions: {
    type: Array,
    default: () => [],
  },
  appAccess: {
    type: String,
  },
  useCustomHasPermission: {
    type: Boolean,
  },
  customHasPermission: {
    type: Boolean,
  },
  useCustomOrgAccess: {
    type: Boolean,
  },
  customOrgAccess: {
    type: Boolean,
    required: false,
  },
  hidePageHeader: {
    type: Boolean,
    default: false,
  },
  hideSideBar: {
    type: Boolean,
    default: false,
  },
  hideTopBar: {
    type: Boolean,
    default: false,
  },
  hidePadding: {
    type: Boolean,
    default: false,
  },
  hideBottomNav: {
    type: Boolean,
    default: false,
  },
  loginRedirectionRoute: {
    type: String,
    required: false,
  },
  hideSupportBanner: {
    type: Boolean,
    default: false,
  },
});

const {
  routes,
  publicRoutes,
  useCustomHasPermission,
  customHasPermission,
  useCustomOrgAccess,
  customOrgAccess,
  hidePageHeader,
  hideSideBar,
  hideTopBar,
  hideBottomNav,
} = toRefs(props);
const { appPermissions, appAccess } = props;
const showChangeClient = ref(false);
const changingClient = ref(false);
const settingAssumeUser = ref(false);
const multiOrgUserSettingOrg = ref(false);
const loggedIn = ref(false);

const hydrating = ref(false);
const originalRoute = ref(undefined);

Amplify.configure(awsExports);
Auth.configure(awsExports);
const store = useRootStore();

const { hydrate } = store;

const { user, userId, orgId, orgName, failedToLoad, isCrewMember, showNavBar } =
  storeToRefs(store);

Hub.listen('auth', async ({ payload: { event } }) => {
  switch (event) {
    case 'signIn':
      unsetV3LogoutCookie();
      await Promise.all([hydrateUserInfo(), getRum()]); // Start rum after logging in

      if (isString(originalRoute.value)) {
        router.push({ path: originalRoute.value });
        originalRoute.value = undefined;
      } else if (isString(props.loginRedirectionRoute)) {
        router.push({ path: props.loginRedirectionRoute });
      }

      break;
    case 'signOut':
      // Set the v3 cookie to inform MyQ v2 that v3 has logged out
      setV3LogoutCookie();
      eidLogout();
      store.reset();
      sessionStorage.removeItem('datapassDismissedUntilLogin');
      localStorage.clear();
      window.location.href = '/';

      break;
    default:
      break;
  }
});

const { orgHasApp } = usePermissions(appAccess);
const { hasPermission } = usePermissions(appPermissions);

const mcdUrl = computed(() => {
  return getMcDSignInURL();
});

const currentYear = computed(() => {
  return `${new Date().getFullYear()}`;
});

/** */
async function beforeMountHandler() {
  loggedIn.value = await isLoggedIn();
  if (!loggedIn.value) {
    const currentLocale = ref(getBrowserLocale());

    toggleLocale(currentLocale.value);

    const pathname = window.location.pathname;
    const baseRoute = import.meta.env.BASE_URL;

    const re = new RegExp(`^${baseRoute}`);
    const oldRoute = pathname.replace(re, '');
    const currentRoute = router?.currentRoute?.value;

    if (oldRoute !== currentRoute?.fullPath) {
      originalRoute.value = oldRoute;
    }

    return;
  }

  store.hydrate();
  await store.waitForUserToBeLoaded();
  await checkTOS();
  await checkEmailPopup();
}
onBeforeMount(beforeMountHandler);

const isPublicRoute = computed(() => {
  const pubRoutes = router.publicRoutes ?? [];

  // console.log({
  //   router,
  //   route,
  //   pubRoutes,
  //   isPublicRoute: pubRoutes.includes(route.path),
  // });

  return pubRoutes.includes(route.path);
});

/** */
async function checkTOS() {
  try {
    const mismatch = await checkTOSMismatch(orgId.value, userId.value);
    if (mismatch) {
      showTOS.value = true;
    }
  } catch (error) {
    console.error('error checking TOS', error);
  }
}

async function checkEmailPopup() {
  if (
    !user.value.displayData.alertEmail &&
    user.value.displayData.email.includes('@users.myqsrsoft.com')
  ) {
    try {
      const result = await accountsGet(
        `accounts/checkEmailPopup?userId=${encodeURIComponent(
          user.value.userId,
        )}`,
      );
      showEmailPopup.value = result.showEmailPopup;
    } catch (error) {
      console.error('Error checking email popup:', error);
    }
  }
}

const currentLocale = ref(getBrowserLocale() || 'en');
const translateButtonLabel = computed(() =>
  currentLocale.value === 'en' ? 'Traducir a Español' : 'Translate to English',
);

async function toggleLocale(initialLocale) {
  let newLocale;
  if (initialLocale) {
    newLocale = initialLocale;
  } else {
    newLocale = currentLocale.value === 'en' ? 'es' : 'en';
  }
  await setLocale(newLocale);
  currentLocale.value = newLocale;
  I18n.setLanguage(newLocale);
  I18n.putVocabulariesForLanguage(newLocale, {
    ...amplifyTranslations[newLocale],
    email: t('email'),
    password: t('password'),
    'Reset Password': t('reset-password'),
    username: t('username'),
    'enter-your-username': t('enter-your-username'),
    'forgot-your-password': t('forgot-your-password'),
    'Password does not conform to policy: Password not long enough': t(
      'password-not-long-enough',
    ),
    'Password does not conform to policy: Password must have uppercase characters':
      t('password-must-have-uppercase-characters'),
    'Password does not conform to policy: Password must satisfy regular expression pattern: ^\\S.*\\S$':
      t('password-not-long-enough'),
    'Code *': t('code'),
  });
  refreshKey.value++;
}

async function hydrateUserInfo() {
  hydrating.value = true;
  try {
    await hydrate();
  } catch (e) {
    console.error(e);
  }
  hydrating.value = false;
}

function reload() {
  // reload the page
  window.location.reload();
}

async function isLoggedIn() {
  try {
    await Auth.currentSession();
    return true;
  } catch (e) {
    return false;
  }
}

const hidePageHeaderComputed = computed(() => {
  if (hidePageHeader.value) return true;
  return router.currentRoute.value.name === '404';
});

watch(routes, () => {
  if (!routes.value?.length) return;
  addRoutes(routes.value);
  // push the current path to the router
  const path = window.location.pathname;
  router.push(path);
});

watch(
  publicRoutes,
  () => {
    if (!publicRoutes.value?.length) return;
    addPublicRoutes(publicRoutes.value);
  },
  { immediate: true },
);

const pageHeader = ref();
const pageHeaderRight = computed(() => pageHeader?.value?.headerRight);
const pageHeaderCenter = computed(() => pageHeader?.value?.headerCenter);
provide('pageHeaderRight', pageHeaderRight);
provide('pageHeaderCenter', pageHeaderCenter);

const topNav = ref();
const topNavLeft = computed(() => topNav?.value?.topNavLeft);
provide('topNavLeft', topNavLeft);

const pageHeightSubtractor = computed(() => {
  if (hidePageHeader.value) return '0px';
  return '64px';
});
</script>

<i18n>
  {
    "en": {
      "we-make-it-easier-to-run-great-restaurants": "We make it easier to run great restaurants",
      "forgot-your-password": "Forgot your password?",
      "or": "OR",
      "sign-in-with-mcd-eid": "Sign in with McD eID",
      "sign-in-with-magic-link": "Sign in with Magic Link",
      "all-rights-reserved": "All rights reserved",
      "confidential": "Confidential",
      "failed-to-load-please-click-this-button": "Failed to load, please click this button",
      "reload": "Reload",
      "wait-a-minute-while-we-load-your-app": "Wait a minute while we load your app",
      "your-org-does-not-have-access-to-this-application": "Your org does not have access to this application",
      "you-do-not-have-access-to-this-application": "You do not have access to this application",
      "email": "Email",
      "password": "Password",
      "reset-password": "Reset Password",
      "username": "Username",
      "enter-your-username": "Enter your username",
      "password-not-long-enough": "Your password must be at least 12 characters long.",
      "password-must-have-uppercase-characters": "Your password must contain at least one uppercase letter.",
      "code": "Code",
    },
    "es": {
      "we-make-it-easier-to-run-great-restaurants": "Hacemos que sea más fácil gestionar grandes restaurantes",
      "forgot-your-password": "¿Olvidaste tu contraseña?",
      "or": "O",
      "sign-in-with-mcd-eid": "Iniciar Sesión con McD eID",
      "sign-in-with-magic-link": "Iniciar Sesión con Enlace Mágico",
      "all-rights-reserved": "Todos los derechos reservados",
      "confidential": "QSRSoft, LLC Confidencial",
      "failed-to-load-please-click-this-button": "No se pudo cargar, por favor haga clic en este botón",
      "reload": "Recargar",
      "wait-a-minute-while-we-load-your-app": "Espera un minuto mientras cargamos tu aplicación",
      "your-org-does-not-have-access-to-this-application": "Tu organización no tiene acceso a esta aplicación",
      "you-do-not-have-access-to-this-application": "No tienes acceso a esta aplicación",
      "email": "Correo Electrónico",
      "password": "Contraseña",
      "reset-password": "Restablecer Contraseña",
      "username": "Usuario",
      "enter-your-username": "Introduce tu nombre de usuario",
      "password-not-long-enough": "Tu contraseña debe tener al menos 12 caracteres.",
      "password-must-have-uppercase-characters": "Tu contraseña debe tener al menos una letra mayúscula.",
      "code": "Código",
    }
  }
</i18n>

<style scoped lang="scss">
.page-container {
  // height: calc(100% - 64px);
  // if hidePageHeader, then 100%, otherwise 100% - 64px
  height: calc(100% - v-bind(pageHeightSubtractor));
}
.top-nav {
  display: flex;
  gap: 10px;
  margin: 10px;
}

.loading-container {
  width: 100%;
  height: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinnerContainer {
  flex-direction: column;
}

:deep([data-amplify-authenticator]) {
  color: black;
  height: 100%;
  z-index: 1;
  position: relative;
}

:deep(button.amplify-button--primary) {
  background: rgb(var(--v-theme-primary));
}

.currentColor {
  color: white;
  text-shadow:
    -1px -1px 0 black,
    1px -1px 0 black,
    -1px 1px 0 black,
    1px 1px 0 black;
}

.center-text {
  text-align: center;
}

$buttonContainerPadding: var(--amplify-components-authenticator-form-padding);

.dividerContainer,
:deep(.magicLinkButtonContainer .dividerContainer) {
  padding: 0 $buttonContainerPadding;
}

.buttonContainer,
:deep(.magicLinkButtonContainer .buttonContainer) {
  padding: $buttonContainerPadding;
  display: flex;
  flex-direction: column;
  text-decoration: none;
}

.eidButton,
:deep(.magicLinkButtonContainer .eidButton) {
  background: black;
  color: white;
}

.dividerWithText,
:deep(.magicLinkButtonContainer .dividerWithText) {
  width: 100%;
  text-align: center;
  border-bottom: 1px solid #000;
  line-height: 0.1em;
  margin: 10px 0;
}

.dividerWithText span,
:deep(.magicLinkButtonContainer .dividerWithText span) {
  background: #fff;
  padding: 0 10px;
}

.forgot-password-button {
  width: 100%;
  text-align: center;
}

.changing-client-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
